import { useMemo } from 'react'
import { create } from 'zustand'
import type { OrganizationStats, Property } from '../types/api.types'
import { platformOrgService } from '../services/platformOrgService'
import { propertiesService } from '../services/propertiesService'

interface PlatformFilterState {
  organizations: OrganizationStats[]
  allProperties: Property[]
  loading: boolean
  initialized: boolean

  selectedOrgType: string | null
  selectedOrgId: number | null
  selectedPropertyId: number | null

  fetchAll: () => Promise<void>
  refreshOrgs: () => Promise<void>
  selectOrgType: (type: string | null) => void
  selectOrg: (id: number | null) => void
  selectProperty: (id: number | null) => void
}

export const usePlatformFilterStore = create<PlatformFilterState>((set, get) => ({
  organizations: [],
  allProperties: [],
  loading: false,
  initialized: false,

  selectedOrgType: null,
  selectedOrgId: null,
  selectedPropertyId: null,

  fetchAll: async () => {
    if (get().initialized || get().loading) return
    set({ loading: true })
    try {
      const [orgs, props] = await Promise.all([
        platformOrgService.getAll(),
        propertiesService.getAll(),
      ])
      set({ organizations: orgs, allProperties: props, loading: false, initialized: true })
    } catch {
      set({ loading: false })
    }
  },

  refreshOrgs: async () => {
    try {
      const orgs = await platformOrgService.getAll()
      set({ organizations: orgs })
    } catch { /* silent */ }
  },

  selectOrgType: (type) => {
    set({ selectedOrgType: type, selectedOrgId: null, selectedPropertyId: null })
  },

  selectOrg: (id) => {
    set({ selectedOrgId: id, selectedPropertyId: null })
  },

  selectProperty: (id) => {
    set({ selectedPropertyId: id })
  },
}))

// ── Derived selectors (useMemo prevents new-reference infinite loops) ──

export function useOrgTypes(): string[] {
  const organizations = usePlatformFilterStore((s) => s.organizations)
  return useMemo(
    () => Array.from(new Set(organizations.map((o) => o.organization_type).filter(Boolean) as string[])),
    [organizations],
  )
}

export function useFilteredOrgs(): OrganizationStats[] {
  const organizations = usePlatformFilterStore((s) => s.organizations)
  const selectedOrgType = usePlatformFilterStore((s) => s.selectedOrgType)
  return useMemo(
    () => (!selectedOrgType ? organizations : organizations.filter((o) => o.organization_type === selectedOrgType)),
    [organizations, selectedOrgType],
  )
}

export function useFilteredProperties(): Property[] {
  const allProperties = usePlatformFilterStore((s) => s.allProperties)
  const selectedOrgId = usePlatformFilterStore((s) => s.selectedOrgId)
  return useMemo(
    () => (!selectedOrgId ? allProperties : allProperties.filter((p) => p.organization_id === selectedOrgId)),
    [allProperties, selectedOrgId],
  )
}
